import { Fragment, useEffect, type ReactNode } from 'react';
import styled from 'styled-components';
import type {
  KfoControlKind,
  KfoGuarantee,
  KfoReport,
  KfoReportKind,
  KfoRfiSource,
  KfoRfiStatus,
  KfoValidation,
} from '@shared/api';
import {
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
  paginationWindow,
} from '@shared/ui/RegistryShell';

// Общие стили и помощники раздела «КФО» (docs/pto-kfo-spec.md §8).

// ─────────── Подписи ───────────

export const REPORT_KIND_LABEL: Record<KfoReportKind, string> = {
  smr: 'Отчёт СМР',
  smr_bolts: 'Отчёт СМР — болтовые соединения',
  rfi_acceptance: 'Приложение к RFI — приёмочный',
  rfi_geodetic: 'Приложение к RFI — геодезический',
};

export const REPORT_KINDS: KfoReportKind[] = ['smr', 'smr_bolts', 'rfi_acceptance', 'rfi_geodetic'];

export const isRfiKind = (k: KfoReportKind) => k === 'rfi_acceptance' || k === 'rfi_geodetic';

export const CONTROL_KIND_LABEL: Record<KfoControlKind, string> = {
  acceptance: 'Приёмочный',
  geodetic: 'Геодезический',
  other: 'Прочий',
};

export const RFI_STATUS_LABEL: Record<KfoRfiStatus, string> = {
  draft: 'черновик',
  submitted: 'предъявлен',
  accepted: 'принят',
  rejected: 'отклонён',
};

export const SOURCE_LABEL: Record<KfoRfiSource, string> = {
  customer: 'Заказчик',
  osnova: 'ОСНОВА',
};

/** Почему ГО закрыто (§12). */
export const GUARANTEE_CLOSED_LABEL: Record<string, string> = {
  registered_by_customer: 'учтено Заказчиком',
  id_accepted: 'ИД приняты',
};

/** ГО закрыто: есть причина или дата закрытия. Закрытое просроченным не подсвечивается. */
export function isGuaranteeClosed(g: Pick<KfoGuarantee, 'closed_reason' | 'closed_at'>): boolean {
  return !!g.closed_reason || !!g.closed_at;
}

/** Просрочено и ещё открыто. */
export function isGuaranteeOverdue(
  g: Pick<KfoGuarantee, 'overdue' | 'closed_reason' | 'closed_at'>,
): boolean {
  return g.overdue && !isGuaranteeClosed(g);
}

/** «ИД приняты · 12.09.2026» — подпись закрытого ГО; пустая строка у открытого. */
export function guaranteeClosedText(g: Pick<KfoGuarantee, 'closed_reason' | 'closed_at'>): string {
  if (!isGuaranteeClosed(g)) return '';
  const reason = g.closed_reason
    ? (GUARANTEE_CLOSED_LABEL[g.closed_reason] ?? g.closed_reason)
    : 'закрыто';
  return g.closed_at ? `${reason} · ${fmtDate(g.closed_at)}` : reason;
}

/** «Отчёт СМР за 15.07.2025 № 12». */
export function reportTitle(r: Pick<KfoReport, 'kind' | 'report_date' | 'number'>): string {
  const no = r.number ? ` № ${r.number}` : '';
  return `${REPORT_KIND_LABEL[r.kind] ?? r.kind} за ${fmtDate(r.report_date)}${no}`;
}

// ─────────── Числа и даты ───────────

/** Число по-русски (пробел-разделитель тысяч, запятая), без хвостовых нулей. */
export function fmtNum(v: number | null | undefined, digits = 3): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: digits });
}

/** `YYYY-MM-DD` → `ДД.ММ.ГГГГ`; прочее — как есть. */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('ru-RU');
}

/** Сегодня в локальной зоне, `YYYY-MM-DD` (для полей `<input type="date">`). */
export function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Разбор введённого объёма: пусто → 0 (позиция удаляется), запятая как
 * десятичный разделитель, пробелы-разделители тысяч игнорируются.
 * `null` — ввод не число (в том числе отрицательное: объём периода ≥ 0).
 */
export function parseQty(s: string): number | null {
  const t = s.replace(/\s/g, '').replace(',', '.');
  if (t === '') return 0;
  if (!/^\d*\.?\d+$|^\d+\.$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Строка для поля ввода объёма из числа (запятая, без разделителей тысяч). */
export function qtyToInput(v: number | null | undefined): string {
  if (v === null || v === undefined || v === 0) return '';
  return String(v).replace('.', ',');
}

// ─────────── Ошибки API ───────────

export function apiStatus(err: unknown): number | string | undefined {
  return (err as { status?: number | string } | undefined)?.status;
}

/** 404 на эндпоинте, которого ещё нет на сервере (этапы 2–3) — показываем заглушку. */
export function isNotFound(err: unknown): boolean {
  return apiStatus(err) === 404;
}

/** Человекочитаемая причина ошибки (`{error:{message}}` бэкенда или nginx). */
export function errorText(err: unknown, fallback: string): string {
  const data = (err as { data?: unknown } | undefined)?.data as
    { error?: { message?: string } | string; message?: string } | undefined;
  if (data) {
    if (typeof data.error === 'string' && data.error) return data.error;
    if (typeof data.error === 'object' && data.error?.message) return data.error.message;
    if (typeof data.message === 'string' && data.message) return data.message;
  }
  if (apiStatus(err) === 'FETCH_ERROR') return 'Нет связи с сервером. Повторите позже.';
  return fallback;
}

/** Ответ 422 выпуска с результатом проверок (`KfoValidation`), если он есть. */
export function validationFrom(err: unknown): KfoValidation | null {
  const data = (err as { data?: unknown } | undefined)?.data as Partial<KfoValidation> | undefined;
  if (data && Array.isArray(data.errors)) {
    return { errors: data.errors, warnings: Array.isArray(data.warnings) ? data.warnings : [] };
  }
  return null;
}

/** Сохранить файл: имя из `Content-Disposition` (если сервер прислал) или запасное. */
export function saveBlob(blob: Blob, fallbackName: string): void {
  const name = blob instanceof File && blob.name ? blob.name : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Имя файла без символов, недопустимых в Windows. */
export function safeFileName(s: string): string {
  return s.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_');
}

// ─────────── Раскладка ───────────

export const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const PanelTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 16px;
`;

export const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(4)};
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

/** Подписанное поле формы: подпись сверху, контрол снизу. */
export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  & > label {
    margin-bottom: 0;
  }
`;

export const SmallSelect = styled.select`
  height: 34px;
  padding: 0 ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  max-width: 220px;
`;

export const SmallInput = styled.input`
  height: 34px;
  padding: 0 ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

/** Поле объёма прямо в строке таблицы (как колонка «Физический объем» у Заказчика). */
export const QtyInput = styled.input<{ $invalid?: boolean; $warn?: boolean; $pending?: boolean }>`
  width: 92px;
  height: 26px;
  padding: 0 6px;
  text-align: right;
  font: inherit;
  font-size: 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme, $pending }) => ($pending ? theme.colors.primarySoft : theme.colors.background)};
  border: 1px solid
    ${({ theme, $invalid, $warn }) =>
      $invalid
        ? theme.colors.status.danger
        : $warn
          ? theme.colors.status.warning
          : theme.colors.border};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const TableWrap = styled.div<{ $maxHeight?: string }>`
  overflow: auto;
  max-height: ${({ $maxHeight }) => $maxHeight ?? '64vh'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

/** Плотная таблица реестров КФО: липкая шапка, числа вправо, подсветка строк. */
export const DataTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 5px 7px;
    text-align: left;
    vertical-align: top;
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-weight: 600;
    white-space: nowrap;
  }
  td.num,
  th.num {
    text-align: right;
    white-space: nowrap;
  }
  td.nowrap {
    white-space: nowrap;
  }
  td.wrap {
    min-width: 220px;
    max-width: 380px;
  }
  tbody tr[data-click='true'] {
    cursor: pointer;
  }
  tbody tr:hover td {
    background: ${({ theme }) => theme.colors.surface};
  }
  tbody tr[data-tone='danger'] td {
    background: ${({ theme }) => theme.colors.status.danger}14;
  }
  tbody tr[data-tone='warning'] td {
    background: ${({ theme }) => theme.colors.status.warning}14;
  }
  tbody tr[data-tone='muted'] td {
    opacity: 0.65;
  }
  tfoot td {
    font-weight: 600;
    background: ${({ theme }) => theme.colors.surface};
  }
  /* Колонка ввода объёма прикреплена к правому краю: таблица широкая, а поле
     должно быть под рукой без горизонтальной прокрутки. */
  th.stick-r,
  tbody tr td.stick-r {
    position: sticky;
    right: 0;
    opacity: 1;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    box-shadow: inset 1px 0 0 ${({ theme }) => theme.colors.border};
  }
  th.stick-r {
    z-index: 2;
  }
`;

export const Banner = styled.div<{ $tone?: 'info' | 'warning' | 'danger' | 'success' }>`
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'success'
          ? theme.colors.status.success
          : $tone === 'info'
            ? theme.colors.status.info
            : theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.5;
`;

/** Маленькая плашка-признак в ячейке («нет в срезе», «Прораб»). */
export const Tag = styled.span<{ $tone?: 'neutral' | 'warning' | 'danger' | 'success' | 'info' }>`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.radius.pill};
  white-space: nowrap;
  letter-spacing: 0.02em;
  color: ${({ theme, $tone }) =>
    $tone === 'danger'
      ? theme.colors.status.danger
      : $tone === 'warning'
        ? theme.colors.status.warning
        : $tone === 'success'
          ? theme.colors.status.success
          : $tone === 'info'
            ? theme.colors.status.info
            : theme.colors.text.secondary};
  border: 1px solid currentColor;
`;

export const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const Tab = styled.button<{ $active: boolean }>`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.secondary)};
`;

/** Пара «подпись — значение» в карточке строки. */
export const KvGrid = styled.dl`
  display: grid;
  grid-template-columns: minmax(150px, 38%) 1fr;
  gap: 4px 12px;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 13px;

  dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  dd {
    margin: 0;
    word-break: break-word;
  }
`;

export const Stat = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  min-width: 130px;

  b {
    display: block;
    font-size: 18px;
  }
  span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const Stats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

// ─────────── Пагинация (страницы с 0 в UI, с 1 — на сервере) ───────────

export const PER_PAGE_OPTIONS = [50, 100, 200];

export function Pager({
  page,
  perPage,
  total,
  onPage,
  onPerPage,
  unit = 'строк',
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (p: number) => void;
  onPerPage?: (n: number) => void;
  unit?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const win = paginationWindow(Math.min(page, pages - 1), pages);
  return (
    <RegistryPagination>
      <span>
        Всего {unit}: {total.toLocaleString('ru-RU')} · стр. {Math.min(page, pages - 1) + 1} из{' '}
        {pages}
      </span>
      <RegistryPageButtons>
        <RegistryPageBtn type="button" disabled={page <= 0} onClick={() => onPage(page - 1)}>
          ←
        </RegistryPageBtn>
        {win.map((p, i) => (
          <Fragment key={p}>
            {i > 0 && p - win[i - 1] > 1 && <span style={{ alignSelf: 'center' }}>…</span>}
            <RegistryPageBtn type="button" $active={p === page} onClick={() => onPage(p)}>
              {p + 1}
            </RegistryPageBtn>
          </Fragment>
        ))}
        <RegistryPageBtn
          type="button"
          disabled={page >= pages - 1}
          onClick={() => onPage(page + 1)}
        >
          →
        </RegistryPageBtn>
        {onPerPage && (
          <SmallSelect
            value={perPage}
            onChange={(e) => onPerPage(Number(e.target.value))}
            aria-label="Строк на странице"
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                по {n}
              </option>
            ))}
          </SmallSelect>
        )}
      </RegistryPageButtons>
    </RegistryPagination>
  );
}

// ─────────── Диалоги ───────────

const Backdrop = styled.div<{ $side?: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: ${({ theme }) => theme.z.modal};
  display: flex;
  align-items: ${({ $side }) => ($side ? 'stretch' : 'center')};
  justify-content: ${({ $side }) => ($side ? 'flex-end' : 'center')};
  padding: ${({ theme, $side }) => ($side ? 0 : theme.spacing(4))};
`;

const DialogBox = styled.div<{ $width: number }>`
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xl};
  padding: ${({ theme }) => theme.spacing(6)};
  width: min(${({ $width }) => $width}px, 100%);
  max-height: 92vh;
  overflow: auto;
  box-shadow: ${({ theme }) => theme.shadow.lg};
`;

const DrawerBox = styled.aside`
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  width: min(760px, 100vw);
  height: 100%;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing(5)};
  box-shadow: ${({ theme }) => theme.shadow.lg};
`;

const DialogHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  h2 {
    margin: 0;
    font-size: 19px;
    font-weight: 600;
  }
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
}

/** Модальное окно шире стандартного `Modal` — под таблицы позиций. */
export function Dialog({
  open,
  title,
  onClose,
  width = 960,
  children,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  width?: number;
  children: ReactNode;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <Backdrop onClick={onClose}>
      <DialogBox $width={width} role="dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHead>
          <h2>{title}</h2>
          <CloseBtn type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </CloseBtn>
        </DialogHead>
        {children}
      </DialogBox>
    </Backdrop>
  );
}

/** Боковая панель (карточка строки КФО). */
export function Drawer({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <Backdrop $side onClick={onClose}>
      <DrawerBox role="dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHead>
          <h2>{title}</h2>
          <CloseBtn type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </CloseBtn>
        </DialogHead>
        {children}
      </DrawerBox>
    </Backdrop>
  );
}
